import React, { FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { linkIcon as LinkIcon } from "../../assets/icons";
import AutoResizableTextarea from "../../components/AutoResizableTextarea/AutoResizableTextarea";
import DragAndDropField from "../../components/DragAndDropField/DragAndDropField";
import IOSCompatibleInput from "../../components/IOSCompatibleInput/IOSCompatibleInput";
import {
  StudentAssignmentTestForm,
  StudentAssignmentTestResult,
} from "../../components/StudentAssignmentTest/StudentAssignmentTest";
import { useNotify } from "../../context/NotificationContext";
import {
  fetchStudentAssignment,
  submitStudentAssignment,
  submitStudentAssignmentTest,
} from "../../services/studentPortalService/studentPortalService";
import type {
  StudentAssignment,
  StudentAssignmentStatus,
} from "../../services/studentPortalService/studentPortalService.types";
import { getServerErrorData } from "../../utils/httpError";
import { logger } from "../../utils/logger";
import {
  AssignmentAnswerMeta,
  AssignmentError,
  AssignmentField,
  AssignmentForm,
  AssignmentFormError,
  AssignmentFormFoot,
  AssignmentFormNote,
  AssignmentHint,
  AssignmentLabel,
  AssignmentMeta,
  AssignmentPageShell,
  AssignmentPlainText,
  AssignmentReview,
  AssignmentReviewFrom,
  AssignmentReviewHead,
  AssignmentReviewRating,
  AssignmentRichText,
  AssignmentSection,
  AssignmentSectionHeading,
  AssignmentSubmitButton,
  AssignmentTextareaField,
  AssignmentTitle,
  AssignmentTitleRow,
  AssignmentUrlField,
  AssignmentWaiting,
} from "./StudentAssignmentPage.styles";
import StudentBreadcrumbs from "./StudentBreadcrumbs";
import {
  DemoStatusChip,
  MediaAction,
  MediaIcon,
  MediaMain,
  MediaName,
  MediaRowLink,
  MediaSub,
  type StudentStatusTone,
} from "./StudentCourseLesson.styles";
import StudentMediaList from "./StudentMediaList";
import StudentVideoSplitView, { type StudentVideoSelection } from "./StudentVideoSplitView";
import { EmptyState, LoadingState } from "./studentPortal.styles";

const STATUS: Record<StudentAssignmentStatus, { label: string; tone: StudentStatusTone }> = {
  not_submitted: { label: "Не отправлено", tone: "neutral" },
  review: { label: "На проверке", tone: "info" },
  revision: { label: "Нужна доработка", tone: "warning" },
  rated: { label: "Проверено", tone: "success" },
};

const formatResponseDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("ru-RU");
};

const draftFromAssignment = (assignment: StudentAssignment) =>
  assignment.status === "revision"
    ? {
        text: assignment.response?.text || "",
        video_url: assignment.response?.video_url || "",
      }
    : { text: "", video_url: "" };

const StudentAssignmentPage: React.FC = () => {
  const { assignmentId } = useParams();
  const id = Number(assignmentId);
  const [assignment, setAssignment] = useState<StudentAssignment | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<StudentVideoSelection | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [draft, setDraft] = useState({ text: "", video_url: "" });
  const [files, setFiles] = useState<File[]>([]);
  const [testSelections, setTestSelections] = useState<Record<number, number[]>>({});
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const toast = useNotify();

  useEffect(() => {
    setSelectedVideo(null);
  }, [id]);

  useEffect(() => {
    if (!id) return undefined;
    let active = true;
    fetchStudentAssignment(id)
      .then((data) => {
        if (!active) return;
        setAssignment(data);
        setDraft(draftFromAssignment(data));
        setFiles([]);
        setTestSelections({});
        setExternalErrors({});
      })
      .catch((error) => {
        logger.error("Ошибка загрузки задания:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить задание", life: 3000 });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, toast]);

  if (loading) return <LoadingState>Загружаем задание…</LoadingState>;
  if (!assignment) return <EmptyState>Задание недоступно.</EmptyState>;

  const status = STATUS[assignment.status];
  const response = assignment.response;
  const review = response?.review || null;
  const canSubmit = assignment.status === "not_submitted" || assignment.status === "revision";
  const isRated = assignment.status === "rated";
  const showReview = Boolean(review) && (assignment.status === "revision" || isRated);
  const showAnswer = Boolean(response) && assignment.status !== "not_submitted";
  const hasTest = Boolean(assignment.test);
  const allTestQuestionsAnswered = assignment.test
    ? assignment.test.questions.every((question) => (testSelections[question.id] || []).length > 0)
    : true;

  const assignmentMaterialLink = assignment.video_url ? (
    <MediaRowLink href={assignment.video_url} target="_blank" rel="noreferrer">
      <MediaIcon><LinkIcon aria-hidden="true" /></MediaIcon>
      <MediaMain>
        <MediaName>Дополнительный материал</MediaName>
        <MediaSub>ссылка</MediaSub>
      </MediaMain>
      <MediaAction>Открыть</MediaAction>
    </MediaRowLink>
  ) : null;

  const responseLink = response?.video_url ? (
    <MediaRowLink href={response.video_url} target="_blank" rel="noreferrer">
      <MediaIcon><LinkIcon aria-hidden="true" /></MediaIcon>
      <MediaMain>
        <MediaName>Ссылка на работу</MediaName>
        <MediaSub>{response.video_url}</MediaSub>
      </MediaMain>
      <MediaAction>Открыть</MediaAction>
    </MediaRowLink>
  ) : null;

  const updateDraft = (field: "text" | "video_url", value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setExternalErrors((current) => {
      if (!current[field] && !current.detail) return current;
      const next = { ...current };
      delete next[field];
      delete next.detail;
      return next;
    });
  };

  const handleTestSelection = (questionId: number, optionIds: number[]) => {
    setTestSelections((current) => ({ ...current, [questionId]: optionIds }));
    setExternalErrors((current) => {
      if (!current.test_answers && !current.detail) return current;
      const next = { ...current };
      delete next.test_answers;
      delete next.detail;
      return next;
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit || submitting) return;

    if (assignment.test && !allTestQuestionsAnswered) {
      setExternalErrors({ test_answers: "Ответьте на все вопросы теста" });
      return;
    }
    if (!assignment.test && !draft.text.trim() && !draft.video_url.trim() && files.length === 0) {
      setExternalErrors({ detail: "Добавьте комментарий, ссылку на работу или файл" });
      return;
    }

    setSubmitting(true);
    setExternalErrors({});
    try {
      const extras = {
        text: draft.text,
        video_url: draft.video_url,
        files,
      };
      const updated = assignment.test
        ? await submitStudentAssignmentTest(assignment.id, {
            ...extras,
            answers: assignment.test.questions.map((question) => ({
              question_id: question.id,
              option_ids: testSelections[question.id] || [],
            })),
          })
        : await submitStudentAssignment(assignment.id, extras);
      setAssignment(updated);
      setDraft(draftFromAssignment(updated));
      setFiles([]);
      setTestSelections({});
      toast.show({
        severity: "success",
        summary: "Готово",
        detail: assignment.test ? "Тест отправлен преподавателю" : "Домашнее задание отправлено",
        life: 3000,
      });
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formatted: Record<string, string> = {};
        Object.entries(serverErrors).forEach(([key, value]) => {
          formatted[key] = String(value);
        });
        setExternalErrors(formatted);
      } else {
        logger.error("Ошибка отправки домашнего задания:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось отправить домашнее задание", life: 3000 });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StudentVideoSplitView video={selectedVideo} onClose={() => setSelectedVideo(null)}>
      <AssignmentPageShell>
        <StudentBreadcrumbs
          items={[
            { label: "Мои курсы", to: "/student/courses" },
            { label: assignment.course_title, to: `/student/courses/${assignment.course_id}` },
            {
              label: assignment.lesson_title,
              to: `/student/courses/${assignment.course_id}/lessons/${assignment.lesson}`,
            },
            { label: assignment.title },
          ]}
        />

        <AssignmentTitleRow>
          <AssignmentTitle>{assignment.title}</AssignmentTitle>
          <DemoStatusChip $tone={status.tone}>{status.label}</DemoStatusChip>
        </AssignmentTitleRow>
        <AssignmentMeta>Домашнее задание к уроку {assignment.lesson_order}</AssignmentMeta>

        <AssignmentSectionHeading>Что нужно сделать</AssignmentSectionHeading>
        <AssignmentSection>
          <AssignmentRichText dangerouslySetInnerHTML={{ __html: assignment.content }} />
          <StudentMediaList
            items={assignment.files_from_storage}
            assignmentId={assignment.id}
            variant="lesson"
            leadingItem={assignmentMaterialLink}
            selectedVideoId={selectedVideo?.id}
            onVideoSelect={(video) =>
              setSelectedVideo({
                ...video,
                contextLabel: "Видео из материалов задания. Форму ответа можно заполнять параллельно с просмотром.",
              })
            }
          />
        </AssignmentSection>

        {showReview && review ? (
          <>
            <AssignmentSectionHeading>
              {isRated ? "Проверка" : "Замечания преподавателя"}
            </AssignmentSectionHeading>
            <AssignmentReview $rated={isRated}>
              <AssignmentReviewHead>
                <AssignmentReviewFrom $rated={isRated}>
                  {isRated ? "Проверено преподавателем" : "Что нужно поправить"}
                </AssignmentReviewFrom>
                {review.rating !== null ? (
                  <AssignmentReviewRating>
                    Оценка <strong>{review.rating} из 5</strong>
                  </AssignmentReviewRating>
                ) : null}
              </AssignmentReviewHead>
              <AssignmentPlainText>{review.teacher_comments}</AssignmentPlainText>
            </AssignmentReview>
          </>
        ) : null}

        {canSubmit ? (
          <>
            <AssignmentSectionHeading>
              {assignment.status === "revision" ? "Исправьте и отправьте снова" : hasTest ? "Пройдите тест" : "Ваш ответ"}
            </AssignmentSectionHeading>
            <AssignmentSection>
              <AssignmentForm onSubmit={handleSubmit}>
                {externalErrors.detail || externalErrors.non_field_errors || externalErrors.test_answers ? (
                  <AssignmentFormError>
                    {externalErrors.detail || externalErrors.non_field_errors || externalErrors.test_answers}
                  </AssignmentFormError>
                ) : null}

                {assignment.test ? (
                  <StudentAssignmentTestForm
                    test={assignment.test}
                    selections={testSelections}
                    onChange={handleTestSelection}
                    disabled={submitting}
                  />
                ) : null}

                <AssignmentField>
                  <AssignmentLabel htmlFor="student-assignment-text">
                    {hasTest ? "Комментарий к ответу · необязательно" : "Комментарий"}
                  </AssignmentLabel>
                  <AssignmentTextareaField $hasError={Boolean(externalErrors.text)}>
                    <AutoResizableTextarea
                      id="student-assignment-text"
                      value={draft.text}
                      onChange={(event) => updateDraft("text", event.target.value)}
                      placeholder="Что сделали и на что обратить внимание"
                      disabled={submitting}
                    />
                  </AssignmentTextareaField>
                  {externalErrors.text ? <AssignmentError>{externalErrors.text}</AssignmentError> : null}
                </AssignmentField>

                <AssignmentField>
                  <AssignmentLabel htmlFor="student-assignment-video">Ссылка на работу или видео</AssignmentLabel>
                  <AssignmentUrlField>
                    <IOSCompatibleInput
                      id="student-assignment-video"
                      value={draft.video_url}
                      onChange={(event) => updateDraft("video_url", event.target.value)}
                      placeholder="https://"
                      disabled={submitting}
                      hasError={Boolean(externalErrors.video_url)}
                      errorMessage={externalErrors.video_url}
                    />
                  </AssignmentUrlField>
                  <AssignmentHint>Например, ссылка на файл в Фигме или на запись экрана.</AssignmentHint>
                </AssignmentField>

                <AssignmentField>
                  <AssignmentLabel as="span">Файлы</AssignmentLabel>
                  <DragAndDropField
                    variant="compact"
                    placeholder="Перетащите файлы сюда"
                    infoText="или нажмите, чтобы выбрать. До пяти файлов"
                    files={files}
                    maxFiles={5}
                    onFileSelected={(selected) => setFiles(selected || [])}
                    disabled={submitting}
                  />
                  {externalErrors.files ? <AssignmentError>{externalErrors.files}</AssignmentError> : null}
                </AssignmentField>

                <AssignmentFormFoot>
                  <AssignmentSubmitButton
                    type="submit"
                    disabled={submitting || Boolean(assignment.test && !allTestQuestionsAnswered)}
                  >
                    {submitting
                      ? "Отправляем…"
                      : assignment.status === "revision"
                        ? "Отправить доработку"
                        : hasTest
                          ? "Отправить тест"
                          : "Отправить решение"}
                  </AssignmentSubmitButton>
                  <AssignmentFormNote>
                    После отправки работа уйдёт преподавателю на проверку.
                  </AssignmentFormNote>
                </AssignmentFormFoot>
              </AssignmentForm>
            </AssignmentSection>
          </>
        ) : null}

        {assignment.status === "review" ? (
          <>
            <AssignmentSectionHeading>Статус</AssignmentSectionHeading>
            <AssignmentWaiting>
              Работа у преподавателя. Когда её проверят, здесь появятся комментарий и оценка.
            </AssignmentWaiting>
          </>
        ) : null}

        {showAnswer && response ? (
          <>
            <AssignmentSectionHeading>
              {assignment.status === "revision" ? "Прошлая версия" : "Отправленный ответ"}
            </AssignmentSectionHeading>
            <AssignmentSection>
              <AssignmentAnswerMeta>Отправлено {formatResponseDate(response.created)}</AssignmentAnswerMeta>
              {response.test_attempt ? <StudentAssignmentTestResult attempt={response.test_attempt} /> : null}
              {response.text ? <AssignmentPlainText>{response.text}</AssignmentPlainText> : null}
              <StudentMediaList
                items={response.files_from_storage}
                variant="lesson"
                leadingItem={responseLink}
              />
            </AssignmentSection>
          </>
        ) : null}
      </AssignmentPageShell>
    </StudentVideoSplitView>
  );
};

export default StudentAssignmentPage;
